      /* XB Compare*/
/*
  Robust XB Compare integration:
  - Replace compare placeholders with custom.logo_image (DOM first, AJAX fallback)
  - Intercept "View" (original Add-to-Cart) buttons using capture phase and redirect
  - Reapplies on dynamic DOM updates
*/
(function () {
  'use strict';

  if (!document.querySelector('.product-card-compare, xb-button-comparison, .xb-popup-modal__product-compare-wrapper')) {
    return;
  }

  // CONFIG
  const IMAGE_SELECTORS = [
    '.xb-list-product-comparison__product-item img',
    'xb-button-comparison img',
    '.xb-table-compare__product-in-table-image'
  ].join(',');

  const VIEW_BUTTON_SELECTOR = '.xb-table-compare__product-in-table-button-add';
  const PRODUCT_LINK_SELECTOR = 'a.xb-table-compare__product-in-table-link[href], a[href*="/products/"]';

  // caches
  const logoMap = {}; // handle -> logoURL
  const processingButtons = new WeakSet();
  const compareProductDataCache = {};
  const compareProductContentCache = {};
  const KEY_ROWS_FALLBACK_LIMIT = 7;
  const COMPARE_KEY_FIELDS = [
    { key: 'price', label: 'Pricing', priority: 220, pattern: /\b(price|pricing|cost|fee|amount)\b/ },
    { key: 'plan', label: 'Plan', priority: 210, pattern: /\b(plan|tier|package|edition)\b/ },
    { key: 'features', label: 'Features', priority: 206, pattern: /\b(features?|feature set|highlights?)\b/ },
    { key: 'billing', label: 'Billing', priority: 200, pattern: /\b(billing|subscription|renewal|license|contract|monthly|annual|yearly|trial)\b/ },
    { key: 'pros_cons', label: 'Pros & Cons', priority: 198, pattern: /\b(pros?\s*&\s*cons|pros?\s+and\s+cons|advantages|disadvantages|benefits|drawbacks)\b/ },
    { key: 'availability', label: 'Availability', priority: 195, pattern: /\b(availability|stock|inventory|status|ready|uptime)\b/ },
    { key: 'category', label: 'Category', priority: 185, pattern: /\b(category|type|product type|service type|hosting type|model)\b/ },
    { key: 'performance', label: 'Performance', priority: 180, pattern: /\b(cpu|processor|cores?|speed|performance|throughput|bandwidth)\b/ },
    { key: 'capacity', label: 'Capacity', priority: 175, pattern: /\b(storage|capacity|ram|memory|space|disk|traffic|visits|users?|seats?)\b/ },
    { key: 'support', label: 'Support', priority: 170, pattern: /\b(support|sla|managed|response time|help desk)\b/ },
    { key: 'security', label: 'Security', priority: 165, pattern: /\b(security|ssl|backup|firewall|protection|compliance)\b/ },
    { key: 'deployment', label: 'Deployment', priority: 160, pattern: /\b(deployment|hosting|server|location|region|setup|provisioning)\b/ },
    { key: 'compatibility', label: 'Compatibility', priority: 155, pattern: /\b(integration|compatibility|platform|os|framework|cms|control panel)\b/ },
    { key: 'brand', label: 'Brand', priority: 145, pattern: /\b(vendor|brand|provider|publisher)\b/ },
    { key: 'variants', label: 'Options', priority: 135, pattern: /\b(variant|option|version|edition|color|size)\b/ },
    { key: 'coverage', label: 'Coverage', priority: 125, pattern: /\b(warranty|guarantee|refund|return)\b/ },
    { key: 'sku', label: 'SKU', priority: 115, pattern: /\b(sku|part number|model number)\b/ }
  ];
  const COMPARE_IGNORE_PATTERNS = [
    /\b(image|photo|thumbnail|gallery|preview)\b/,
    /\b(description|overview|details|summary|about|content)\b/,
    /^(title|name|product name|product title)$/i,
    /\b(handle|slug|id|created|updated|published)\b/,
    /\b(barcode|gtin|isbn|ean|upc)\b/,
    /\b(compare|action|remove|delete|button)\b/,
    /\b(collection|tag|tags)\b/
  ];

  // util: extract handle from element (link href / xb-handle / data-xb-handle / data-product-handle)
  function extractHandle(el) {
    if (!el) return null;

    // check for ancestor link to /products/
    const link = el.closest('a[href*="/products/"]');
    if (link) {
      try {
        const href = link.getAttribute('href') || link.href || '';
        const m = href.match(/\/products\/([^\/\?\#]+)/);
        if (m) return m[1];
      } catch (e) {}
    }

    // attributes on element or ancestors
    const attrEl = el.closest('[xb-handle], [data-xb-handle], [data-product-handle], [data-handle]');
    if (attrEl) {
      return attrEl.getAttribute('xb-handle') || attrEl.getAttribute('data-xb-handle') ||
             attrEl.getAttribute('data-product-handle') || attrEl.getAttribute('data-handle') || null;
    }

    return null;
  }

  // Build logoMap from DOM product-card logos
  function buildLogoMapFromDOM() {
    // Look for images that have data-product-handle or class product-card-logo
    const domImgs = Array.from(
      document.querySelectorAll('img.product-card-logo, img.product-card-logo__image, img[data-product-handle], [data-logo-image]')
    );
    let count = 0;
    domImgs.forEach(img => {
      const handle = img.getAttribute('data-product-handle') || img.closest('[data-product-handle]')?.getAttribute('data-product-handle');
      const src = img.getAttribute('data-logo-image') || img.getAttribute('src') || img.src;
      if (handle && src) {
        logoMap[handle] = src;
        count++;
      }
    });
    return count > 0;
  }

  // AJAX fallback: fetch product JSON and return custom.logo_image (if present)
  async function fetchLogoViaAjax(handle) {
    const mappedLogo = window.ProductLogoMap?.[handle];
    if (mappedLogo) return mappedLogo;

    const compareContent = await fetchCompareProductContent(handle);
    return compareContent?.logo_url || null;
  }

  const COMPARE_SELECTION_STORAGE_KEY = 'itmart_compare_selection';

  function normalizeCompareText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[_:]+/g, ' ')
      .trim();
  }

  function toTitleCase(value) {
    return String(value || '')
      .toLowerCase()
      .split(' ')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  function getCompareFieldDefinition(label) {
    const normalized = normalizeCompareText(label);
    if (!normalized) {
      return { key: '', label: '', priority: 0, ignored: true };
    }

    if (COMPARE_IGNORE_PATTERNS.some((pattern) => pattern.test(normalized))) {
      return { key: '', label: toTitleCase(label), priority: 0, ignored: true };
    }

    for (const field of COMPARE_KEY_FIELDS) {
      if (field.pattern.test(normalized)) {
        return { key: field.key, label: field.label, priority: field.priority, ignored: false };
      }
    }

    return { key: '', label: toTitleCase(label), priority: 0, ignored: false };
  }

  function getCompareRowElement(heading) {
    if (!heading) return null;

    let current = heading;
    while (current && current !== document.body) {
      const children = Array.from(current.children || []);
      const containsHeading = children.some((child) => child === heading || child.contains(heading));
      if (containsHeading && children.length >= 2) {
        return current;
      }
      current = current.parentElement;
    }

    return heading.parentElement || null;
  }

  function getCompareRowCellElements(row, heading) {
    if (!row) return [];

    const children = Array.from(row.children || []).filter((child) => child !== heading && !child.contains(heading));
    if (children.length) return children;

    return Array.from(row.querySelectorAll('td, [class*="cell"], [class*="value"]')).filter((cell) => cell !== heading && !cell.contains(heading));
  }

  function getCompareRowValues(row, heading) {
    return getCompareRowCellElements(row, heading)
      .map((cell) => normalizeCompareText(cell.textContent))
      .filter(Boolean);
  }

  function hasMeaningfulCompareValues(values) {
    return values.some((value) => value && value !== '-' && value !== 'n/a' && value !== 'na' && value !== 'none');
  }

  function getCompareValueFingerprint(value) {
    return normalizeCompareText(value)
      .replace(/[.,]/g, '')
      .replace(/\b(yes|available|true|included|active|in stock)\b/g, 'yes')
      .replace(/\b(no|unavailable|false|excluded|inactive|out of stock)\b/g, 'no');
  }

  function areCompareValuesUniform(values) {
    const normalized = values
      .map((value) => getCompareValueFingerprint(value))
      .filter(Boolean);

    return normalized.length > 1 && normalized.every((value) => value === normalized[0]);
  }

  function collectCompareRows(modal) {
    const rows = [];
    const seenRows = new Set();

    modal.querySelectorAll('.xb-table-compare__cell-heading').forEach((heading) => {
      const rawLabel = (heading.textContent || '').trim();
      if (!rawLabel) return;

      const row = getCompareRowElement(heading);
      if (!row || seenRows.has(row)) return;
      seenRows.add(row);

      const field = getCompareFieldDefinition(rawLabel);
      const values = getCompareRowValues(row, heading);

      rows.push({
        row,
        heading,
        rawLabel,
        displayLabel: row.dataset.itmartCompareLabel || field.label || rawLabel,
        key: row.dataset.itmartCompareKey || field.key,
        priority: Number(row.dataset.itmartPriority || field.priority || 0),
        ignored: field.ignored,
        values
      });
    });

    return rows;
  }

  function isCustomCompareRow(row) {
    return Boolean(row?.hasAttribute?.('data-itmart-custom-row'));
  }

  function getCompareProductHandles(modal) {
    const cards = Array.from(modal.querySelectorAll('.xb-table-compare__product-in-table, .xb-list-product-comparison__product-item'));
    return cards.map((card) => extractHandle(card)).filter(Boolean);
  }

  function getBaseCompareTemplateRow(modal) {
    const rows = collectCompareRows(modal);
    return rows.find((item) => (
      !item.row.hasAttribute('data-itmart-custom-row') &&
      getCompareRowCellElements(item.row, item.heading).length > 0
    )) || null;
  }

  function escapeCompareHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function extractCompareListItemsFromHtml(html) {
    if (!html) return [];

    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild || doc.body;
    const listItems = Array.from(root.querySelectorAll('li'))
      .map((item) => (item.textContent || '').replace(/\s+/g, ' ').trim())
      .filter(Boolean);

    if (listItems.length) {
      return listItems;
    }

    return Array.from(root.querySelectorAll('p'))
      .map((item) => (item.textContent || '').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
  }

  function extractCompareProsConsFromHtml(html) {
    const empty = { pros: [], cons: [] };
    if (!html) return empty;

    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild || doc.body;
    const sections = { pros: [], cons: [] };
    let activeKey = '';

    Array.from(root.children).forEach((node) => {
      const text = normalizeCompareText(node.textContent);
      if (!text) return;

      if (/(^|\b)pros?(\b|:)/.test(text)) {
        activeKey = 'pros';
      } else if (/(^|\b)cons?(\b|:)/.test(text)) {
        activeKey = 'cons';
      }

      if ((node.matches('ul, ol') || node.querySelector('li')) && activeKey) {
        const items = Array.from(node.querySelectorAll('li'))
          .map((item) => (item.textContent || '').replace(/\s+/g, ' ').trim())
          .filter(Boolean);
        sections[activeKey].push(...items);
      }
    });

    if (!sections.pros.length && !sections.cons.length) {
      const lists = Array.from(root.querySelectorAll('ul, ol'));
      if (lists[0]) {
        sections.pros = Array.from(lists[0].querySelectorAll('li'))
          .map((item) => (item.textContent || '').replace(/\s+/g, ' ').trim())
          .filter(Boolean);
      }
      if (lists[1]) {
        sections.cons = Array.from(lists[1].querySelectorAll('li'))
          .map((item) => (item.textContent || '').replace(/\s+/g, ' ').trim())
          .filter(Boolean);
      }
    }

    return sections;
  }

  function buildCompareRichListMarkup(items, emptyText) {
    const safeItems = (items || []).filter(Boolean).slice(0, 6);
    if (!safeItems.length) {
      return `<span class="itmart-compare-empty">${escapeCompareHtml(emptyText)}</span>`;
    }

    return `
      <div class="itmart-compare-rich-card">
        <ul class="itmart-compare-rich-list">
          ${safeItems.map((item) => `<li>${escapeCompareHtml(item)}</li>`).join('')}
        </ul>
      </div>
    `;
  }

  function getCompareProductStrip(modal) {
    const cards = Array.from(modal.querySelectorAll('.xb-table-compare__product-in-table, .xb-list-product-comparison__product-item'));
    if (cards.length < 2) return null;

    let current = cards[0].parentElement;
    while (current && current !== modal) {
      const directCardCount = Array.from(current.children || []).filter((child) => (
        child.matches?.('.xb-table-compare__product-in-table, .xb-list-product-comparison__product-item')
      )).length;

      if (directCardCount >= 2) {
        return current;
      }

      current = current.parentElement;
    }

    return cards[0].parentElement || null;
  }

  function hideNativeCompareProductRows(modal) {
    const rows = collectCompareRows(modal);
    rows.forEach((item) => {
      const hasProductCard = item.row.querySelector('.xb-table-compare__product-in-table, .xb-list-product-comparison__product-item');
      if (hasProductCard && !isCustomCompareRow(item.row)) {
        item.row.classList.add('itmart-native-product-strip-hidden');
        item.row.dataset.itmartCompareField = 'products';
      }
    });

    const productStrip = getCompareProductStrip(modal);
    if (productStrip) {
      productStrip.dataset.itmartCompareProductStrip = 'true';
      productStrip.classList.add('itmart-native-product-strip-hidden');
    }
  }

  function formatComparePrice(product) {
    const price = Number(product?.price || 0);
    const currencyCode = window.Shopify?.currency?.active || '{{ shop.currency }}';
    if (!price) return '';

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2
      }).format(price / 100);
    } catch (error) {
      return `${(price / 100).toFixed(2)} ${currencyCode}`;
    }
  }

  function buildCompareHeroCardMarkup(handle, product, content) {
    const logo = content?.logo_url || logoMap[handle] || window.ProductLogoMap?.[handle] || product?.featured_image || product?.images?.[0] || '';
    const price = formatComparePrice(product);
    const safeTitle = escapeCompareHtml(product?.title || handle);
    const safeVendor = escapeCompareHtml(product?.vendor || '');
    const safeUrl = `/products/${encodeURIComponent(handle)}`;

    return `
      <article class="itmart-compare-hero-card" data-product-handle="${escapeCompareHtml(handle)}">
        <div class="itmart-compare-hero-card__logo-wrap">
          ${logo
            ? `<img class="itmart-compare-hero-card__logo" src="${escapeCompareHtml(logo)}" alt="${safeTitle}" loading="lazy">`
            : `<div class="itmart-compare-hero-card__logo itmart-compare-hero-card__logo--placeholder" aria-hidden="true"></div>`}
        </div>
        <div class="itmart-compare-hero-card__body">
          <h3 class="itmart-compare-hero-card__title">${safeTitle}</h3>
          ${safeVendor ? `<p class="itmart-compare-hero-card__vendor">${safeVendor}</p>` : ''}
          ${price ? `<p class="itmart-compare-hero-card__price">${escapeCompareHtml(price)}</p>` : ''}
        </div>
        <a class="itmart-compare-hero-card__cta" href="${safeUrl}">View product</a>
      </article>
    `;
  }

  function closeCompareModal(modal) {
    const scope = modal.parentElement || document;
    const nativeClose = scope.querySelector(
      'button[aria-label*="close" i], button[class*="close"], [role="button"][aria-label*="close" i], .xb-popup-modal__close'
    );

    if (nativeClose) {
      nativeClose.click();
      return;
    }

    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  }

  async function ensureCompareHeroStrip(modal) {
    const bodyContent = modal.querySelector('.xb-popup-modal__product-compare-body-content');
    const handles = getCompareProductHandles(modal);
    if (!bodyContent || !handles.length) return;
    modal.style.setProperty('--itmart-compare-columns', String(Math.max(handles.length, 2)));

    const heroData = await Promise.all(handles.map(async (handle) => {
      const [product, content] = await Promise.all([
        fetchCompareProductData(handle),
        fetchCompareProductContent(handle)
      ]);

      return { handle, product, content };
    }));

    const signature = heroData.map(({ handle, product, content }) => (
      `${handle}|${content?.logo_url || ''}|${product?.price || ''}|${product?.available ? '1' : '0'}`
    )).join('||');

    const legacyRow = modal.querySelector('[data-itmart-custom-row="product_overview"]');
    if (legacyRow) {
      legacyRow.remove();
    }

    const floatingHero = modal.querySelector('.itmart-compare-hero');
    if (floatingHero) {
      floatingHero.remove();
    }

    const overviewRow = upsertCompareCustomRow(modal, {
      key: 'product_overview',
      label: 'Products',
      priority: 999,
      insertAtStart: true,
      cellMarkup: heroData.map(({ handle, product, content }) => buildCompareHeroCardMarkup(handle, product, content))
    });

    if (overviewRow) {
      overviewRow.dataset.signature = signature;
      overviewRow.classList.remove('itmart-is-hidden');
    }

    let headerRow = bodyContent.querySelector('.itmart-compare-header-row');
    if (!headerRow) {
      headerRow = document.createElement('section');
      headerRow.className = 'itmart-compare-header-row';
      bodyContent.insertBefore(headerRow, bodyContent.firstElementChild || null);
    }

    headerRow.style.gridTemplateColumns = `minmax(12rem, 14rem) repeat(${Math.max(handles.length, 2)}, minmax(0, 1fr))`;

    if (headerRow.dataset.signature !== signature) {
      headerRow.innerHTML = `
        <div class="itmart-compare-header-row__label">Products</div>
        ${heroData.map(({ handle, product, content }) => `
          <div class="itmart-compare-header-row__cell" data-product-handle="${escapeCompareHtml(handle)}">
            ${buildCompareHeroCardMarkup(handle, product, content)}
          </div>
        `).join('')}
      `;
      headerRow.dataset.signature = signature;
    }

    hideNativeCompareProductRows(modal);
  }

  function tuneCompareModalContainer(modal) {
    modal.dataset.itmartCompareModal = 'true';
    modal.style.width = '100%';
    modal.style.maxWidth = '100%';
    modal.style.minWidth = '0';
    modal.style.margin = '0 auto';

    const frame = modal.parentElement;
    if (frame) {
      frame.dataset.itmartCompareFrame = 'true';
      frame.style.width = '100%';
      frame.style.maxWidth = '100%';
      frame.style.minWidth = '0';
      frame.style.margin = '0 auto';
    }

    const shell = frame?.parentElement;
    if (shell && shell !== document.body) {
      shell.dataset.itmartCompareShell = 'true';
      shell.style.width = '100%';
      shell.style.maxWidth = '100%';
      shell.style.paddingLeft = '0';
      shell.style.paddingRight = '0';
      shell.style.margin = '0';
    }

    const shellParent = shell?.parentElement;
    if (shellParent && shellParent !== document.body) {
      shellParent.dataset.itmartCompareViewport = 'true';
      shellParent.style.width = '100%';
      shellParent.style.maxWidth = '100%';
      shellParent.style.paddingLeft = '0';
      shellParent.style.paddingRight = '0';
      shellParent.style.margin = '0';
    }
  }

  function getCompareRowScore(item) {
    const textLength = item.values.join(' ').length;
    const customBonus = isCustomCompareRow(item.row) ? 1000 : 0;
    const cardPenalty = item.row.querySelector('.xb-table-compare__product-in-table, .xb-list-product-comparison__product-item') ? -600 : 0;
    return customBonus + item.priority + Math.min(textLength, 400) + cardPenalty;
  }

  function normalizeCompareRows(rows) {
    const duplicatesByKey = new Map();

    rows.forEach((item) => {
      if (!item.key) return;
      if (!duplicatesByKey.has(item.key)) duplicatesByKey.set(item.key, []);
      duplicatesByKey.get(item.key).push(item);
    });

    const priceRows = duplicatesByKey.get('price') || [];
    if (priceRows.length > 1) {
      const sorted = priceRows.slice().sort((a, b) => a.values.join(' ').length - b.values.join(' ').length);
      const primary = sorted[0];
      primary.displayLabel = 'Pricing';
      sorted.slice(1).forEach((item) => {
        item.key = 'plan';
        item.priority = Math.max(item.priority, 210);
        item.displayLabel = 'Plans';
      });
    }

    const featureRows = (duplicatesByKey.get('features') || []).slice().sort((a, b) => getCompareRowScore(b) - getCompareRowScore(a));
    featureRows.slice(1).forEach((item) => {
      item.ignored = true;
    });

    const prosConsRows = (duplicatesByKey.get('pros_cons') || []).slice().sort((a, b) => getCompareRowScore(b) - getCompareRowScore(a));
    prosConsRows.slice(1).forEach((item) => {
      item.ignored = true;
    });
  }

  function buildCompareProsConsMarkup(content) {
    const pros = (content?.pros || []).filter(Boolean).slice(0, 4);
    const cons = (content?.cons || []).filter(Boolean).slice(0, 4);

    return `
      <div class="itmart-compare-pros-cons">
        <section class="itmart-compare-tone-card" data-tone="positive">
          <h4>Pros</h4>
          ${pros.length
            ? `<ul class="itmart-compare-rich-list">${pros.map((item) => `<li>${escapeCompareHtml(item)}</li>`).join('')}</ul>`
            : '<span class="itmart-compare-empty">Pros not available</span>'}
        </section>
        <section class="itmart-compare-tone-card" data-tone="muted">
          <h4>Cons</h4>
          ${cons.length
            ? `<ul class="itmart-compare-rich-list">${cons.map((item) => `<li>${escapeCompareHtml(item)}</li>`).join('')}</ul>`
            : '<span class="itmart-compare-empty">Cons not available</span>'}
        </section>
      </div>
    `;
  }

  async function fetchCompareProductContent(handle) {
    if (!handle) return null;
    if (compareProductContentCache[handle]) {
      return compareProductContentCache[handle];
    }

    compareProductContentCache[handle] = fetch(`/products/${handle}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html'
      }
    })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector('[data-itmart-product-compare-content]');
        if (!node) return null;
        try {
          return JSON.parse(node.textContent || '{}');
        } catch (error) {
          return null;
        }
      })
      .catch(() => null);

    return compareProductContentCache[handle];
  }

  function upsertCompareCustomRow(modal, options) {
    const template = getBaseCompareTemplateRow(modal);
    if (!template) return null;

    const handles = getCompareProductHandles(modal);
    if (!handles.length) return null;

    let row = modal.querySelector(`[data-itmart-custom-row="${options.key}"]`);
    if (!row) {
      row = template.row.cloneNode(true);
      row.dataset.itmartCustomRow = options.key;
      row.classList.remove('itmart-is-hidden');
      if (options.insertAtStart && template.row.parentElement.firstElementChild) {
        template.row.parentElement.insertBefore(row, template.row.parentElement.firstElementChild);
      } else {
        template.row.parentElement.insertBefore(row, template.row.nextSibling);
      }
    }

    const heading = row.querySelector('.xb-table-compare__cell-heading') || row.firstElementChild;
    const cells = getCompareRowCellElements(row, heading);
    if (!heading || !cells.length) return null;

    row.dataset.itmartCompareKey = options.key || '';
    row.dataset.itmartCompareLabel = options.label || '';
    if (options.priority) {
      row.dataset.itmartPriority = String(options.priority);
    }

    heading.textContent = options.label;

    cells.forEach((cell, index) => {
      if (index < handles.length) {
        cell.innerHTML = options.cellMarkup[index] || '<span class="itmart-compare-empty">Not available</span>';
        cell.classList.add('itmart-compare-rich-cell');
        cell.style.display = '';
      } else {
        cell.innerHTML = '';
        cell.style.display = 'none';
      }
    });

    return row;
  }

  async function injectCompareMetafieldRows(modal) {
    const handles = getCompareProductHandles(modal);
    if (!handles.length) return;

    const contentByHandle = await Promise.all(handles.map((handle) => fetchCompareProductContent(handle)));
    const featureMarkup = [];
    const prosConsMarkup = [];
    let hasFeatures = false;
    let hasProsCons = false;

    contentByHandle.forEach((content) => {
      const featureItems = extractCompareListItemsFromHtml(content?.features_html);
      const prosCons = extractCompareProsConsFromHtml(content?.pros_cons_html);

      featureMarkup.push(buildCompareRichListMarkup(featureItems, 'Features not available'));
      prosConsMarkup.push(buildCompareProsConsMarkup(prosCons));

      if (featureItems.length) hasFeatures = true;
      if ((prosCons.pros && prosCons.pros.length) || (prosCons.cons && prosCons.cons.length)) {
        hasProsCons = true;
      }
    });

    const featuresRow = modal.querySelector('[data-itmart-custom-row="features"]');
    const prosConsRow = modal.querySelector('[data-itmart-custom-row="pros_cons"]');

    if (hasFeatures) {
      upsertCompareCustomRow(modal, {
        key: 'features',
        label: 'Features',
        cellMarkup: featureMarkup
      });
    } else if (featuresRow) {
      featuresRow.remove();
    }

    if (hasProsCons) {
      upsertCompareCustomRow(modal, {
        key: 'pros_cons',
        label: 'Pros & Cons',
        cellMarkup: prosConsMarkup
      });
    } else if (prosConsRow) {
      prosConsRow.remove();
    }
  }

  function updateCompareToolbarState(modal, stats) {
    const toolbar = modal.querySelector('.itmart-compare-toolbar');
    if (!toolbar) return;

    const mode = modal.dataset.itmartCompareMode || 'key';
    const onlyDifferences = modal.dataset.itmartCompareDifferences === 'true';

    toolbar.querySelectorAll('.itmart-compare-toggle').forEach((button) => {
      const action = button.getAttribute('data-action');
      const isActive =
        (action === 'key-specs' && mode === 'key') ||
        (action === 'all-specs' && mode === 'all') ||
        (action === 'differences' && onlyDifferences);

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    const productsNode = toolbar.querySelector('[data-role="product-count"]');
    const rowsNode = toolbar.querySelector('[data-role="row-count"]');
    if (productsNode) {
      productsNode.textContent = `${stats.productCount} products`;
    }
    if (rowsNode) {
      rowsNode.textContent = `${stats.visibleRows} ${mode === 'all' ? 'specs' : 'key specs'} visible`;
    }
  }

  function ensureCompareToolbar(modal, stats) {
    let toolbar = modal.querySelector('.itmart-compare-toolbar');
    if (!toolbar) {
      toolbar = document.createElement('section');
      toolbar.className = 'itmart-compare-toolbar';
      toolbar.innerHTML = `
        <div class="itmart-compare-toolbar__intro">
          <span class="itmart-compare-toolbar__eyebrow">Decision Workspace</span>
          <h2 class="itmart-compare-toolbar__title">Compare the options that matter</h2>
          <p class="itmart-compare-toolbar__subtitle">Key buying signals first, cleaner table hierarchy, and faster side-by-side review.</p>
        </div>
        <div class="itmart-compare-toolbar__meta">
          <span class="itmart-compare-chip" data-role="product-count"></span>
          <span class="itmart-compare-chip" data-role="row-count"></span>
        </div>
        <div class="itmart-compare-toolbar__actions">
          <button type="button" class="itmart-compare-toggle" data-action="key-specs">Key specs</button>
          <button type="button" class="itmart-compare-toggle" data-action="all-specs">All specs</button>
          <button type="button" class="itmart-compare-toggle" data-action="differences">Only differences</button>
          <button type="button" class="itmart-compare-close" data-action="close-modal" aria-label="Close compare modal">Close</button>
        </div>
      `;

      const bodyContent = modal.querySelector('.xb-popup-modal__product-compare-body-content');
      modal.insertBefore(toolbar, bodyContent || modal.firstElementChild || null);
    }

    if (!toolbar.dataset.itmartCompareBound) {
      toolbar.addEventListener('click', (event) => {
        const button = event.target.closest('.itmart-compare-toggle, .itmart-compare-close');
        if (!button) return;

        const action = button.getAttribute('data-action');
        if (action === 'key-specs') {
          modal.dataset.itmartCompareMode = 'key';
        } else if (action === 'all-specs') {
          modal.dataset.itmartCompareMode = 'all';
        } else if (action === 'differences') {
          modal.dataset.itmartCompareDifferences = modal.dataset.itmartCompareDifferences === 'true' ? 'false' : 'true';
        } else if (action === 'close-modal') {
          closeCompareModal(modal);
          return;
        }

        const nextStats = curateCompareRows(modal);
        updateCompareToolbarState(modal, nextStats);
      });

      toolbar.dataset.itmartCompareBound = 'true';
    }

    updateCompareToolbarState(modal, stats);
  }

  function curateCompareRows(modal) {
    const rows = collectCompareRows(modal);
    normalizeCompareRows(rows);
    const mode = modal.dataset.itmartCompareMode || 'key';
    const onlyDifferences = modal.dataset.itmartCompareDifferences === 'true';
    const prioritizedRows = rows
      .filter((item) => !item.ignored && item.priority > 0 && hasMeaningfulCompareValues(item.values))
      .sort((a, b) => b.priority - a.priority);
    const fallbackRows = rows.filter((item) => !item.ignored && item.priority === 0 && hasMeaningfulCompareValues(item.values));
    const preferredRows = mode === 'all'
      ? rows.filter((item) => !item.ignored && hasMeaningfulCompareValues(item.values))
      : prioritizedRows.concat(fallbackRows.slice(0, Math.max(0, KEY_ROWS_FALLBACK_LIMIT - prioritizedRows.length)));
    const preferredRowSet = new Set(preferredRows.map((item) => item.row));
    const rowParent = rows[0]?.row?.parentElement;
    let visibleRows = 0;

    if (rowParent) {
      rows
        .slice()
        .sort((a, b) => {
          const aIsHiddenNativeProduct = a.row.classList.contains('itmart-native-product-strip-hidden');
          const bIsHiddenNativeProduct = b.row.classList.contains('itmart-native-product-strip-hidden');
          if (aIsHiddenNativeProduct !== bIsHiddenNativeProduct) return aIsHiddenNativeProduct ? 1 : -1;
          const aCustom = isCustomCompareRow(a.row);
          const bCustom = isCustomCompareRow(b.row);
          if (aCustom !== bCustom) return aCustom ? -1 : 1;
          if (b.priority !== a.priority) return b.priority - a.priority;
          return 0;
        })
        .forEach((item) => {
          rowParent.appendChild(item.row);
        });
    }

    rows.forEach((item) => {
      const isUniform = areCompareValuesUniform(item.values);
      const cellElements = getCompareRowCellElements(item.row, item.heading);
      const columnCount = Math.max(cellElements.length, Number(modal.style.getPropertyValue('--itmart-compare-columns')) || 0, 2);
      const shouldHide =
        item.ignored ||
        !hasMeaningfulCompareValues(item.values) ||
        (mode !== 'all' && !preferredRowSet.has(item.row)) ||
        (onlyDifferences && isUniform);

      item.heading.textContent = item.displayLabel;
      item.row.dataset.itmartCompareField = item.key || 'custom';
      item.row.dataset.itmartCompareDiff = isUniform ? 'same' : 'different';
      item.row.style.display = 'grid';
      item.row.style.gridTemplateColumns = `minmax(12rem, 14rem) repeat(${columnCount}, minmax(0, 1fr))`;
      item.row.style.width = '100%';
      item.row.style.maxWidth = '100%';
      item.row.style.alignItems = 'start';
      cellElements.forEach((cell) => {
        cell.style.minWidth = '0';
        cell.style.width = '100%';
      });
      item.row.classList.toggle('itmart-is-hidden', shouldHide);

      if (!shouldHide) {
        visibleRows += 1;
      }
    });

    const productCount = modal.querySelectorAll('.xb-table-compare__product-in-table, .xb-list-product-comparison__product-item').length;
    modal.classList.toggle('itmart-compare-has-curation', rows.length > 0);

    return {
      productCount,
      visibleRows
    };
  }

  async function fetchCompareProductData(handle) {
    if (!handle) return null;
    if (compareProductDataCache[handle]) {
      return compareProductDataCache[handle];
    }

    compareProductDataCache[handle] = fetch(`/products/${handle}.js`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    return compareProductDataCache[handle];
  }

  function createCompareMetaChip(text, tone) {
    const chip = document.createElement('span');
    chip.className = 'itmart-compare-card-chip';
    if (tone) {
      chip.dataset.tone = tone;
    }
    chip.textContent = text;
    return chip;
  }

  async function enrichCompareProductCards(modal) {
    const cards = Array.from(modal.querySelectorAll('.xb-table-compare__product-in-table, .xb-list-product-comparison__product-item'));
    if (!cards.length) return;

    await Promise.all(cards.map(async (card) => {
      const handle = extractHandle(card);
      if (!handle) return;

      const product = await fetchCompareProductData(handle);
      if (!product) return;

      let meta = card.querySelector('.itmart-compare-card-meta');
      if (!meta) {
        meta = document.createElement('div');
        meta.className = 'itmart-compare-card-meta';
        const actionButton = card.querySelector('.xb-table-compare__product-in-table-button-add');
        if (actionButton && actionButton.parentElement === card) {
          card.insertBefore(meta, actionButton);
        } else {
          card.appendChild(meta);
        }
      }

      const signature = [
        product.vendor || '',
        product.product_type || '',
        product.available ? 'available' : 'unavailable',
        product.variants?.length || 0
      ].join('|');

      if (meta.dataset.signature === signature) return;

      meta.innerHTML = '';
      if (product.vendor) {
        meta.appendChild(createCompareMetaChip(product.vendor, 'neutral'));
      }
      if (product.product_type) {
        meta.appendChild(createCompareMetaChip(product.product_type, 'neutral'));
      }
      meta.appendChild(createCompareMetaChip(product.available ? 'Available now' : 'Currently unavailable', product.available ? 'positive' : 'muted'));
      if (product.variants && product.variants.length > 1) {
        meta.appendChild(createCompareMetaChip(`${product.variants.length} options`, 'accent'));
      }

      meta.dataset.signature = signature;
    }));
  }

  async function enhanceCompareModals() {
    const modals = Array.from(document.querySelectorAll('.xb-popup-modal__product-compare-wrapper'));
    if (!modals.length) return;

    await Promise.all(modals.map(async (modal) => {
      tuneCompareModalContainer(modal);
      if (!modal.dataset.itmartCompareMode) {
        modal.dataset.itmartCompareMode = 'key';
      }
      if (!modal.dataset.itmartCompareDifferences) {
        modal.dataset.itmartCompareDifferences = 'false';
      }

      const initialStats = curateCompareRows(modal);
      ensureCompareToolbar(modal, initialStats);
      await ensureCompareHeroStrip(modal);
      await injectCompareMetafieldRows(modal);
      await ensureCompareHeroStrip(modal);
      const stats = curateCompareRows(modal);
      ensureCompareToolbar(modal, stats);
      await enrichCompareProductCards(modal);
    }));
  }

  function readStoredCompareSelection() {
    try {
      return JSON.parse(localStorage.getItem(COMPARE_SELECTION_STORAGE_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  function writeStoredCompareSelection(selectionMap) {
    try {
      localStorage.setItem(COMPARE_SELECTION_STORAGE_KEY, JSON.stringify(selectionMap));
    } catch (e) {}
  }

  function setAllCompareRowsState(selectionMap) {
    document.querySelectorAll('.product-card-compare[data-product-handle]').forEach((row) => {
      const handle = row.getAttribute('data-product-handle');
      const pendingUntil = Number(row.dataset.comparePendingUntil || '0');
      if (pendingUntil > Date.now()) return;
      const active = Boolean(handle && selectionMap[handle]);
      row.classList.toggle('is-active', active);
      row.dataset.compareSelected = active ? 'true' : 'false';
    });
  }

  function clearAllCompareSelections() {
    writeStoredCompareSelection({});
    setAllCompareRowsState({});
  }

  function getActiveCompareHandlesFromUI() {
    const handles = new Set();
    const selectors = [
      '.xb-list-product-comparison__product-item a[href*="/products/"]',
      '.xb-table-compare__product-in-table a[href*="/products/"]',
      '.xb-popup-modal__product-compare-wrapper a[href*="/products/"]'
    ];

    document.querySelectorAll(selectors.join(',')).forEach((link) => {
      const href = link.getAttribute('href') || link.href || '';
      const match = href.match(/\/products\/([^\/?#]+)/);
      if (match && match[1]) handles.add(match[1]);
    });

    document.querySelectorAll('[xb-handle], [data-product-handle], [data-xb-handle]').forEach((node) => {
      const handle =
        node.getAttribute('xb-handle') ||
        node.getAttribute('data-product-handle') ||
        node.getAttribute('data-xb-handle');
      if (handle && (node.closest('.xb-list-product-comparison__product-item, .xb-table-compare__product-in-table, .xb-popup-modal__product-compare-wrapper') || node.matches('.xb-list-product-comparison__product-item *, .xb-table-compare__product-in-table *'))) {
        handles.add(handle);
      }
    });

    return handles;
  }

  function syncCompareSelectionsFromUI() {
    const activeHandles = getActiveCompareHandlesFromUI();
    const selectionMap = {};
    activeHandles.forEach((handle) => {
      selectionMap[handle] = true;
    });
    writeStoredCompareSelection(selectionMap);
    setAllCompareRowsState(selectionMap);
  }

  function forceCompareCheckboxVisibility(root, host) {
    if (!root) return;
    const compareRow = host ? host.closest('.product-card-compare') : null;
    const compareHandle = compareRow?.getAttribute('data-product-handle');

    function setCompareProxyState(active) {
      if (!compareRow) return;
      compareRow.classList.toggle('is-active', active);
      compareRow.dataset.compareSelected = active ? 'true' : 'false';
    }

    function toggleCompareProxyState() {
      if (!compareRow) return;
      const nextState = !compareRow.classList.contains('is-active');
      compareRow.dataset.comparePendingUntil = String(Date.now() + 1800);
      setCompareProxyState(nextState);
      if (compareHandle) {
        const selectionMap = readStoredCompareSelection();
        selectionMap[compareHandle] = nextState;
        writeStoredCompareSelection(selectionMap);
      }
    }

    function syncCompareProxyState() {
      if (!compareRow) return;

      if (compareHandle) {
        const selectionMap = readStoredCompareSelection();
        if (typeof selectionMap[compareHandle] === 'boolean') {
          setCompareProxyState(selectionMap[compareHandle]);
          return;
        }
      }

      const checkbox = root.querySelector('input[type="checkbox"]');
      const detectedActive = (
        checkbox?.checked ||
        host?.getAttribute('checked') === '' ||
        host?.getAttribute('aria-pressed') === 'true' ||
        root.querySelector('[aria-checked="true"], .active, .is-active, .checked') ||
        host?.matches?.('.active, .is-active, .checked, [aria-checked="true"], [aria-pressed="true"]')
      );

      if (detectedActive) {
        compareRow.dataset.comparePendingUntil = '0';
        setCompareProxyState(true);
        if (compareHandle) {
          const selectionMap = readStoredCompareSelection();
          selectionMap[compareHandle] = true;
          writeStoredCompareSelection(selectionMap);
        }
        return;
      }

      const hasExplicitInactiveSignal = Boolean(
        checkbox === null &&
        host?.getAttribute('aria-pressed') === 'false'
      );

      if (hasExplicitInactiveSignal) {
        compareRow.dataset.comparePendingUntil = '0';
        setCompareProxyState(false);
        if (compareHandle) {
          const selectionMap = readStoredCompareSelection();
          selectionMap[compareHandle] = false;
          writeStoredCompareSelection(selectionMap);
        }
      }
    }

    syncCompareProxyState();

      if (host && !host.dataset.itmartXbCheckboxBound) {
      if (compareRow) {
        compareRow.addEventListener('click', () => {
          toggleCompareProxyState();
          setTimeout(syncCompareSelectionsFromUI, 1200);
          setTimeout(syncCompareSelectionsFromUI, 1800);
        }, true);
      }

      host.addEventListener('change', () => {
        syncCompareSelectionsFromUI();
      });
      root.addEventListener('change', () => {
        syncCompareSelectionsFromUI();
      });
      host.dataset.itmartXbCheckboxBound = 'true';
    }
  }

  function fixCompareCheckboxes() {
    document.querySelectorAll('xb-button-comparison').forEach((el) => {
      try {
        forceCompareCheckboxVisibility(el, el);
        if (el.shadowRoot) {
          forceCompareCheckboxVisibility(el.shadowRoot, el);
        }
      } catch (e) {
        console.error('fixCompareCheckboxes error', e);
      }
    });
  }

  // Replace compare images (DOM-first, AJAX fallback)
  async function replaceCompareImages() {
    const nodes = Array.from(document.querySelectorAll(IMAGE_SELECTORS));
    if (!nodes.length) return;

    await Promise.all(nodes.map(async (node) => {
      try {
        const handle = extractHandle(node);
        if (!handle) return;

        // prefer existing map
        let logo = logoMap[handle];
        if (!logo) {
          // try DOM -> sometimes product-card present only on collection pages
          if (buildLogoMapFromDOM()) {
            logo = logoMap[handle];
          }
        }
        if (!logo) {
          // last resort: AJAX
          logo = await fetchLogoViaAjax(handle);
        }
        if (logo) {
          const imageTarget = node.tagName === 'IMG' ? node : node.querySelector('img');

          if (imageTarget) {
            if (imageTarget.getAttribute('src') !== logo) {
              imageTarget.setAttribute('src', logo);
              imageTarget.removeAttribute('srcset');
              imageTarget.style.objectFit = 'contain';
              if (!imageTarget.getAttribute('alt')) imageTarget.setAttribute('alt', 'Product logo');
            }
          } else {
            node.innerHTML = `<img class="itmart-compare-generated-logo" src="${logo}" alt="Product logo" loading="lazy">`;
          }
        }
      } catch (e) {
        console.error('replaceCompareImages error', e);
      }
    }));
  }

  // Intercept View buttons in CAPTURE phase to stop Add-to-Cart handlers and redirect
  function interceptViewClicksCapture(event) {
    try {
      const btn = event.target.closest(VIEW_BUTTON_SELECTOR);
      if (!btn) return; // not our button

      // if already processed, let it go (but processing check for safety)
      if (processingButtons.has(btn)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        return;
      }

      // find product url
      const container = btn.closest('.xb-table-compare__product-in-table');
      const link = container?.querySelector(PRODUCT_LINK_SELECTOR);
      if (!link) return;

      const url = link.href || link.getAttribute('href');
      if (!url) return;

      // Block any other handlers (this runs before bubble handlers thanks to capture)
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();

      // mark processed
      processingButtons.add(btn);

      // redirect
      window.location.href = url;
    } catch (e) {
      console.error('interceptViewClicksCapture err', e);
    }
  }

  // Also attach a delegation in bubble phase to re-apply for dynamic content (defensive)
  function interceptViewClicksBubble(e) {
    const btn = e.target.closest(VIEW_BUTTON_SELECTOR);
    if (!btn) return;
    if (processingButtons.has(btn)) return;
    // same logic as capture, but capture is primary
    const container = btn.closest('.xb-table-compare__product-in-table');
    const link = container?.querySelector(PRODUCT_LINK_SELECTOR);
    if (!link) return;
    e.preventDefault(); e.stopImmediatePropagation(); e.stopPropagation();
    processingButtons.add(btn);
    window.location.href = link.href || link.getAttribute('href');
  }

  // Master runner: build DOM map and replace images and attach listeners
  async function runAllOnce() {
    try {
      // First try build from DOM
      buildLogoMapFromDOM();
      fixCompareCheckboxes();
      await enhanceCompareModals();
      // Replace images (DOM-first, with AJAX fallback)
      await replaceCompareImages();
      await enhanceCompareModals();
      fixCompareCheckboxes();
      syncCompareSelectionsFromUI();
    } catch (e) {
      console.error('runAllOnce error', e);
    }
  }

  // Start: attach capture listener on document to intercept before XB
  document.addEventListener('click', interceptViewClicksCapture, true); // capture = true
  // Bubble-phase backup
  document.addEventListener('click', interceptViewClicksBubble, false);
  document.addEventListener('click', function (event) {
    const clearButton = event.target.closest('button, a, [role="button"]');
    if (!clearButton) return;

    const label = (clearButton.textContent || '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (label === 'clear all') {
      clearAllCompareSelections();
      setTimeout(syncCompareSelectionsFromUI, 250);
      setTimeout(syncCompareSelectionsFromUI, 700);
    }
  }, true);

  // Mutation observer to watch for XB dynamic changes
  const mo = new MutationObserver(function () {
    // small debounce - call runAllOnce but not too often
    if (window.__xbReplaceTimeout) clearTimeout(window.__xbReplaceTimeout);
    window.__xbReplaceTimeout = setTimeout(() => {
      runAllOnce();
    }, 120);
  });
  mo.observe(document.body, { childList: true, subtree: true });

  // Do initial runs and scheduled fallbacks (in case page loads slowly)
  runAllOnce();
  setTimeout(runAllOnce, 400);
  setTimeout(runAllOnce, 1200);
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('xb-button-comparison').then(() => {
      runAllOnce();
      setTimeout(runAllOnce, 300);
    }).catch(() => {});
  }

  // Safety: periodic refresh (lightweight) in case mutation events missed
  const periodic = setInterval(() => {
    // only run quickly if some compare UI exists
    if (document.querySelector('.xb-list-product-comparison__product-item, .xb-table-compare__product-in-table')) {
      runAllOnce();
    }
  }, 3000);

  // Expose a small debug function for console: window.xbFixStatus()
  window.xbFixStatus = function () {
    return {
      mappedHandles: Object.keys(logoMap).length,
      mapped: logoMap,
      seenPalette: !!document.querySelector('.xb-list-product-comparison__product-item'),
      seenTable: !!document.querySelector('.xb-table-compare__product-in-table')
    };
  };

})();

(function () {
  'use strict';

  const DEFAULT_ANALYTICS_ORIGIN = 'https://vendor.itmart24.com';
  const SESSION_STORAGE_KEY = 'itmart24_session_id';
  const EVENT_BATCH_WINDOW_MS = 1200;
  const tokenCache = new Map();
  const pendingAnalyticsEvents = [];
  let analyticsFlushTimer = null;
  let analyticsFlushPromise = null;

  function normalizeAnalyticsOrigin(value) {
    const normalized = String(value || '').trim();
    if (!normalized) return '';
    return normalized.replace(/\/+$/, '');
  }

  function getProductAnalyticsConfig() {
    const configNode = document.querySelector('[data-itmart-product-analytics]');
    if (!configNode) return null;

    try {
      return JSON.parse(configNode.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  function resolveAnalyticsBaseUrl() {
    const config = getProductAnalyticsConfig();
    const candidates = [
      window.ITMART24_ANALYTICS_API_BASE_URL,
      config && config.apiBaseUrl,
      document.documentElement && document.documentElement.getAttribute('data-itmart-analytics-api-base-url'),
      DEFAULT_ANALYTICS_ORIGIN,
    ];

    for (const candidate of candidates) {
      const normalized = normalizeAnalyticsOrigin(candidate);
      if (normalized) {
        return `${normalized}/api/analytics`;
      }
    }

    return `${DEFAULT_ANALYTICS_ORIGIN}/api/analytics`;
  }

  function buildAnalyticsHeaders(extraHeaders) {
    const baseUrl = resolveAnalyticsBaseUrl();
    const headers = Object.assign({}, extraHeaders || {});

    if (baseUrl.indexOf('ngrok-free.dev') !== -1) {
      headers['ngrok-skip-browser-warning'] = 'true';
    }

    return headers;
  }

  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (character) {
      const random = (crypto.getRandomValues(new Uint8Array(1))[0] % 16) | 0;
      const value = character === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  function ensureSessionId() {
    try {
      let sessionId = localStorage.getItem(SESSION_STORAGE_KEY);

      if (!sessionId) {
        sessionId = generateUUID();
        localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
      }

      window.ITMART24_SESSION_ID = sessionId;
      return sessionId;
    } catch (error) {
      return null;
    }
  }

  async function fetchAnalyticsToken(productId, vendorId, forceRefresh) {
    const cacheKey = `${productId}::${vendorId}`;

    if (!forceRefresh && tokenCache.has(cacheKey)) {
      return tokenCache.get(cacheKey);
    }

    const tokenEndpoint = `${resolveAnalyticsBaseUrl()}/token`;
    const response = await fetch(`${tokenEndpoint}?productId=${encodeURIComponent(productId)}&vendorId=${encodeURIComponent(vendorId)}`, {
      headers: buildAnalyticsHeaders(),
    });

    if (!response.ok) return null;

    const data = await response.json();
    const token = data.success && data.token ? data.token : null;

    if (token) {
      tokenCache.set(cacheKey, token);
    }

    return token;
  }

  async function sendAnalyticsBatch(events, options) {
    const requestOptions = Object.assign(
      {
        method: 'POST',
        headers: buildAnalyticsHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ events }),
      },
      options || {}
    );
    const batchEndpoint = `${resolveAnalyticsBaseUrl()}/batch`;

    try {
      return await fetch(batchEndpoint, requestOptions);
    } catch (error) {
      return null;
    }
  }

  async function flushAnalyticsEvents(options) {
    if (!pendingAnalyticsEvents.length) {
      return null;
    }

    if (analyticsFlushPromise) {
      return analyticsFlushPromise;
    }

    if (analyticsFlushTimer) {
      clearTimeout(analyticsFlushTimer);
      analyticsFlushTimer = null;
    }

    const batch = pendingAnalyticsEvents.splice(0, pendingAnalyticsEvents.length);
    const events = batch.map((entry) => entry.payload);
    const shouldKeepAlive = Boolean(options && options.keepalive) || batch.some((entry) => entry.keepalive);

    analyticsFlushPromise = (async function () {
      let response = await sendAnalyticsBatch(events, shouldKeepAlive ? { keepalive: true } : undefined);

      if (response && response.status === 401) {
        const refreshedEvents = [];

        for (const entry of batch) {
          const refreshedToken = await fetchAnalyticsToken(entry.payload.productId, entry.payload.vendorId, true);

          if (!refreshedToken) {
            continue;
          }

          refreshedEvents.push(Object.assign({}, entry.payload, { token: refreshedToken }));
        }

        if (refreshedEvents.length) {
          response = await sendAnalyticsBatch(refreshedEvents, shouldKeepAlive ? { keepalive: true } : undefined);
        }
      }

      return response;
    })();

    try {
      return await analyticsFlushPromise;
    } finally {
      analyticsFlushPromise = null;
    }
  }

  function scheduleAnalyticsFlush() {
    if (analyticsFlushTimer) {
      return;
    }

    analyticsFlushTimer = setTimeout(function () {
      analyticsFlushTimer = null;
      flushAnalyticsEvents().catch(function () {});
    }, EVENT_BATCH_WINDOW_MS);
  }

  function queueAnalyticsEvent(payload, options) {
    pendingAnalyticsEvents.push({
      payload,
      keepalive: Boolean(options && options.keepalive),
    });

    if (options && options.immediate) {
      return flushAnalyticsEvents({ keepalive: options.keepalive });
    }

    scheduleAnalyticsFlush();
    return Promise.resolve(null);
  }

  function initCollectionImpressionTracking() {
    if (!window.IntersectionObserver) return;

    const productCards = document.querySelectorAll('.analytics-product-card[data-analytics-product-id][data-analytics-vendor-id]');
    if (!productCards.length) return;

    const observedProducts = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(async (entry) => {
          if (!entry.isIntersecting) return;

          const element = entry.target;
          const productId = element.getAttribute('data-analytics-product-id');
          const vendorId = element.getAttribute('data-analytics-vendor-id');

          if (!productId || !vendorId || observedProducts.has(productId)) {
            observer.unobserve(element);
            return;
          }

          observedProducts.add(productId);
          observer.unobserve(element);

          try {
            const token = await fetchAnalyticsToken(productId, vendorId);
            if (!token) return;

            await queueAnalyticsEvent({
              productId,
              vendorId,
              type: 'IMPRESSION',
              source: window.location.pathname,
              token,
              sessionId: window.ITMART24_SESSION_ID,
            });
          } catch (error) {
            console.error('Impression tracking error:', error);
          }
        });
      },
      {
        threshold: 0.4,
      }
    );

    productCards.forEach((card) => observer.observe(card));
  }

  function initProductPageAnalytics() {
    const config = getProductAnalyticsConfig();
    if (!config) return;

    const productId = String(config.productId || '').trim();
    const vendorId = String(config.vendorId || '').trim();

    if (!productId || !vendorId) return;

    const sessionId = window.ITMART24_SESSION_ID || ensureSessionId();
    let analyticsToken = null;

    async function getToken() {
      if (analyticsToken) return analyticsToken;
      analyticsToken = await fetchAnalyticsToken(productId, vendorId);
      return analyticsToken;
    }

    async function track(type, extraPayload) {
      const token = await getToken();
      if (!token) return null;

      const payload = Object.assign(
        {
          productId,
          vendorId,
          type,
          source: window.location.pathname,
          token,
          sessionId,
        },
        extraPayload || {}
      );

      return queueAnalyticsEvent(
        payload,
        type === 'CLICK'
          ? {
              keepalive: true,
              immediate: true,
            }
          : undefined
      );
    }

    track('VIEW').catch((error) => console.error('Analytics error:', error));

    const buttons = document.querySelectorAll('[data-get-now="true"][data-url]');
    buttons.forEach((button) => {
      button.addEventListener('click', function () {
        track('CLICK').catch((error) => console.error('CLICK tracking error:', error));
      });
    });

    const interval = 10000;
    let heartbeatInterval = null;

    async function sendTimeSpent() {
      try {
        await track('TIME_SPENT', {
          duration: interval,
        });
      } catch (error) {
        console.error('TIME_SPENT tracking error:', error);
      }
    }

    function startHeartbeat() {
      if (!heartbeatInterval) {
        heartbeatInterval = setInterval(sendTimeSpent, interval);
      }
    }

    function stopHeartbeat() {
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
        heartbeatInterval = null;
      }
    }

    if (!document.hidden) {
      startHeartbeat();
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stopHeartbeat();
        flushAnalyticsEvents({ keepalive: true }).catch(function () {});
      } else {
        startHeartbeat();
      }
    });

    window.addEventListener('pagehide', function () {
      flushAnalyticsEvents({ keepalive: true }).catch(function () {});
    });
  }

  ensureSessionId();
  initCollectionImpressionTracking();
  initProductPageAnalytics();
})();
